import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '@/components/ErrorAlert';
import { PageHeader } from '@/components/PageHeader';
import { authClient } from '@/lib/auth-client';
import { errorText } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { client, orpc } from '@/lib/orpc';
import { meQuery, useMe } from '@/lib/session';
import { passkeyGrant } from '@/lib/step-up';

export const Route = createFileRoute('/_app/account')({ component: AccountPage });

type MailPref = 'proposal' | 'needsHuman' | 'paused' | 'done' | 'declineDigest';
const MAIL_PREFS: MailPref[] = ['proposal', 'needsHuman', 'paused', 'done', 'declineDigest'];

function Section({ title, lead, children }: { title: string; lead?: string; children: React.ReactNode }) {
  return (
    <section className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{title}</h2>
        {lead && <p className="text-base-content/70 text-sm">{lead}</p>}
        <div className="mt-2">{children}</div>
      </div>
    </section>
  );
}

function AccountPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <PageHeader title={t('account.title')} />
      <ProfileSection />
      <PasskeySection />
      <TokenSection />
      <ConnectionSection />
      <PasswordSection />
    </div>
  );
}

function ProfileSection() {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.name);
  const save = useMutation({
    mutationFn: (patch: Parameters<typeof client.me.update>[0]) => client.me.update(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meQuery().queryKey })
  });
  return (
    <Section title={t('account.profile')}>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate({ name });
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm">{t('account.name')}</span>
          <input className="input input-sm" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </label>
        <button type="submit" className="btn btn-sm" disabled={save.isPending}>
          {t('common.save')}
        </button>
      </form>
      <h3 className="mt-4 mb-2 text-sm font-medium">{t('account.mails')}</h3>
      <ul className="space-y-1">
        {MAIL_PREFS.map((k) => (
          <li key={k}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={me.mailPrefs[k]}
                onChange={(e) => save.mutate({ mailPrefs: { ...me.mailPrefs, [k]: e.target.checked } })}
              />
              {t(`account.mail.${k}`)}
            </label>
          </li>
        ))}
      </ul>
      {save.isSuccess && <p className="text-success mt-2 text-sm">{t('common.saved')}</p>}
      {save.isError && <p className="text-error mt-2 text-sm">{errorText(t, save.error)}</p>}
    </Section>
  );
}

function PasskeySection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const passkeys = useQuery({
    queryKey: ['passkeys'],
    queryFn: async () => {
      const { data, error } = await authClient.passkey.listUserPasskeys();
      if (error) throw new Error(error.message);
      return data ?? [];
    }
  });
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['passkeys'] }), queryClient.invalidateQueries({ queryKey: meQuery().queryKey })]);
  const add = useMutation({
    mutationFn: async () => {
      // A further passkey needs a confirmation with an existing one (a session alone is not enough).
      await passkeyGrant();
      const res = await authClient.passkey.addPasskey({ name: t('passkeySetup.defaultName') });
      if (res?.error) throw new Error(res.error.message);
    },
    onSuccess: refresh
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await passkeyGrant();
      const { error } = await authClient.passkey.deletePasskey({ id });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh
  });
  const list = passkeys.data ?? [];
  return (
    <Section title={t('account.passkeys')} lead={t('account.passkeysLead')}>
      <ul className="mb-3 space-y-1">
        {list.map((p) => (
          <li key={p.id} className="flex items-center gap-2 text-sm">
            <span className="font-medium">{p.name ?? p.deviceType}</span>
            {p.createdAt && <span className="text-base-content/50 text-xs">{formatDate(p.createdAt)}</span>}
            <button type="button" className="btn btn-ghost btn-xs ml-auto" disabled={list.length <= 1} title={list.length <= 1 ? t('account.lastPasskey') : undefined} onClick={() => remove.mutate(p.id)}>
              {t('account.removePasskey')}
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn-sm" onClick={() => add.mutate()} disabled={add.isPending}>
        {t('account.addPasskey')}
      </button>
      {(add.isError || remove.isError) && <p className="text-error mt-2 text-sm">{t('passkeySetup.failed')}</p>}
    </Section>
  );
}

function TokenSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tokens = useQuery(orpc.apiTokens.list.queryOptions());
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => client.apiTokens.create({ name, expiresInDays: 365 }),
    onSuccess: async (res) => {
      setSecret(res.secret);
      setName('');
      await queryClient.invalidateQueries({ queryKey: orpc.apiTokens.key() });
    }
  });
  const revoke = useMutation({
    mutationFn: (id: string) => client.apiTokens.revoke({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.apiTokens.key() })
  });
  return (
    <Section title={t('account.tokens')} lead={t('account.tokensLead')}>
      {secret && (
        <div role="status" className="alert alert-warning alert-soft mb-3 flex-col items-start text-sm">
          <span>{t('account.tokenOnce')}</span>
          <code className="bg-base-100 rounded p-2 break-all" data-testid="token-secret">
            {secret}
          </code>
          <button type="button" className="btn btn-xs" onClick={() => void navigator.clipboard?.writeText(secret)}>
            {t('common.copy')}
          </button>
        </div>
      )}
      <ul className="mb-3 space-y-1">
        {tokens.data?.map((tk) => (
          <li key={tk.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{tk.name}</span>
            <code className="text-xs">{tk.prefix}…</code>
            <span className="text-base-content/50 text-xs">{tk.revokedAt ? t('account.revoked') : t('account.expires', { date: formatDate(tk.expiresAt) })}</span>
            {!tk.revokedAt && (
              <button type="button" className="btn btn-ghost btn-xs ml-auto" onClick={() => revoke.mutate(tk.id)}>
                {t('account.revoke')}
              </button>
            )}
          </li>
        ))}
      </ul>
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <input className="input input-sm" placeholder={t('account.tokenName')} aria-label={t('account.tokenName')} value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
        <button type="submit" className="btn btn-sm" disabled={create.isPending}>
          {t('account.createToken')}
        </button>
      </form>
      {create.isError && <p className="text-error mt-2 text-sm">{errorText(t, create.error)}</p>}
    </Section>
  );
}

function ConnectionSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const list = useQuery(orpc.connections.list.queryOptions());
  const revoke = useMutation({
    mutationFn: (id: string) => client.connections.revoke({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.connections.key() })
  });
  return (
    <Section title={t('account.connections')} lead={t('account.connectionsLead')}>
      {list.isError && <ErrorAlert error={list.error} />}
      {list.data && !list.data.length && <p className="text-base-content/60 text-sm">{t('account.noConnections')}</p>}
      <ul className="space-y-1">
        {list.data?.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{c.name ?? c.clientId}</span>
            <span className="badge badge-ghost badge-xs">{c.scopes.filter((s) => s === 'assistant' || s === 'worker').join(', ')}</span>
            <span className="text-base-content/50 text-xs">{t('account.granted', { date: formatDate(c.grantedAt) })}</span>
            <button type="button" className="btn btn-ghost btn-xs ml-auto" onClick={() => revoke.mutate(c.id)}>
              {t('account.revoke')}
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { error } = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: true });
    setState(error ? 'failed' : 'done');
    if (!error) {
      setCurrent('');
      setNext('');
    }
  };
  return (
    <Section title={t('account.password')}>
      <form className="flex flex-wrap items-end gap-2" onSubmit={submit}>
        <label className="block">
          <span className="mb-1 block text-sm">{t('account.currentPassword')}</span>
          <input className="input input-sm" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm">{t('account.newPassword')}</span>
          <input className="input input-sm" type="password" autoComplete="new-password" minLength={12} value={next} onChange={(e) => setNext(e.target.value)} required />
        </label>
        <button type="submit" className="btn btn-sm">
          {t('account.changePassword')}
        </button>
      </form>
      {state === 'done' && <p className="text-success mt-2 text-sm">{t('account.passwordChanged')}</p>}
      {state === 'failed' && <p className="text-error mt-2 text-sm">{t('account.passwordFailed')}</p>}
    </Section>
  );
}
